import React, { useEffect, useState, useMemo } from "react";
import { ShoppingBag, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import Reveal from "../components/Reveal";

const eur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + " €";

export default function BoutiquePage() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("__all");

  useEffect(() => {
    document.title = "Boutique · Domaine de La Bégaudière";
    api.get("/boutique").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const shown = cat === "__all" ? products : products.filter((p) => p.category === cat);

  return (
    <div className="bg-cream min-h-screen" data-testid="boutique-page">
      <section className="bg-gradient-to-br from-brand-900 via-[#123240] to-ink-900 text-white pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-brand-100 font-accent text-2xl italic mb-4">
              <ShoppingBag size={20} /> {t("boutiquePage.tag")}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-5">{t("boutiquePage.title")}</h1>
            <p className="text-white/75 font-body text-lg leading-relaxed">{t("boutiquePage.subtitle")}</p>
            <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white/85">
              <MapPin size={15} /> {t("boutiquePage.note")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="boutique-filters">
              {[["__all", t("boutiquePage.allCategories")], ...categories.map((c) => [c, c])].map(([v, l]) => (
                <button key={v} onClick={() => setCat(v)} data-testid={`boutique-filter-${v}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${cat === v ? "bg-brand text-white" : "bg-white border border-black/5 text-ink-600 hover:border-brand"}`}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {shown.length === 0 ? (
            <p className="text-center text-ink-400 py-16" data-testid="boutique-empty">{t("boutiquePage.empty")}</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 70}>
                  <article className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_32px_rgba(44,48,48,0.06)] hover:shadow-[0_16px_48px_rgba(44,48,48,0.12)] transition-shadow h-full flex flex-col" data-testid={`product-card-${p.id}`}>
                    <div className="relative aspect-[4/3] overflow-hidden bg-beige">
                      {p.image ? (
                        <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-ink-300"><ShoppingBag size={40} /></div>
                      )}
                      {p.category && <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-medium text-ink-700">{p.category}</span>}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-heading text-xl text-ink-900">{p.name}</h3>
                      {p.description && <p className="mt-1.5 text-sm text-ink-500 leading-relaxed flex-1">{p.description}</p>}
                      <div className="mt-4 flex items-center justify-between">
                        <span className="font-heading text-2xl text-brand" data-testid={`product-price-${p.id}`}>{eur(p.price)}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs text-brand font-medium"><MapPin size={12} /> À l'accueil</span>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
